/**
 * Detects new frontend deployments and reloads once so users don't need a hard refresh.
 * Works alongside the service worker update flow.
 */

const BUILD_VERSION_KEY = 'maveshi_app_build_version';
const RELOAD_GUARD_KEY = 'maveshi_app_reload_guard';
const CHECK_INTERVAL_MS = 5 * 60 * 1000;

let checkInFlight = false;

function reloadOnce(reason) {
  if (sessionStorage.getItem(RELOAD_GUARD_KEY) === '1') return;
  sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
  if (reason) {
    // eslint-disable-next-line no-console
    console.info('[Maveshi] Loading an app update…', reason);
  }
  window.location.reload();
}

export async function checkForDeployUpdate() {
  if (process.env.NODE_ENV !== 'production') return;
  if (checkInFlight) return;
  checkInFlight = true;

  try {
    const url = `${process.env.PUBLIC_URL || ''}/build-meta.json?_=${Date.now()}`;
    const response = await fetch(url, { cache: 'no-store', credentials: 'same-origin' });
    if (!response.ok) return;

    const meta = await response.json();
    const remoteVersion = meta?.version;
    if (!remoteVersion) return;

    const storedVersion = localStorage.getItem(BUILD_VERSION_KEY);
    if (!storedVersion) {
      localStorage.setItem(BUILD_VERSION_KEY, remoteVersion);
      return;
    }

    if (storedVersion !== remoteVersion) {
      localStorage.setItem(BUILD_VERSION_KEY, remoteVersion);
      reloadOnce('build-meta');
    }
  } catch {
    // offline or missing file — ignore
  } finally {
    checkInFlight = false;
  }
}

export function initDeployUpdateGuard() {
  if (process.env.NODE_ENV !== 'production') return;

  checkForDeployUpdate();

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkForDeployUpdate();
    }
  });

  window.setInterval(checkForDeployUpdate, CHECK_INTERVAL_MS);
}

export function initStaleAssetRecovery() {
  if (process.env.NODE_ENV !== 'production') return;

  const isStaleAssetError = (message) => {
    if (!message) return false;
    const text = String(message);
    return (
      text.includes('ChunkLoadError')
      || text.includes('Loading chunk')
      || text.includes('Failed to fetch dynamically imported module')
      || text.includes('Importing a module script failed')
    );
  };

  window.addEventListener(
    'error',
    (event) => {
      const target = event.target;
      if (target && target.tagName === 'SCRIPT' && target.src) {
        reloadOnce('script-load');
      }
    },
    true,
  );

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason?.message || event.reason;
    if (isStaleAssetError(reason)) {
      event.preventDefault();
      reloadOnce('chunk');
    }
  });
}
